import { EmailOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
  Paper,
  alpha,
  useTheme
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MailOutline as MailOutlineIcon } from "@mui/icons-material";
import axios from "axios";
import { Formik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { API_BASE_URL } from "../../../config/apiBase";
import { adminAuthHeaders, getApiErrorMessage } from "../../../utils/adminApi";
import { clearAdminSession, getAdminIdFromToken } from "../../../utils/adminSession";
import { adminScrollPageSx } from "../../../theme/adminLayout";

const ChangeEmail = () => {
  const theme = useTheme();
  
  // Match the same blue color scheme from Dashboard
  const colors = {
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
    grey: {
      100: "#ffffff",
      200: "#f8f8f8",
      300: "#f0f0f0",
      400: "#e8e8e8",
      500: "#e0e0e0",
      600: "#cccccc",
      700: "#999999",
      800: "#666666",
      900: "#333333",
    }
  };
  
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const handleFormSubmit = async (values) => {
    const adminId = getAdminIdFromToken();
    if (!adminId) {
      setAlertSeverity("error");
      setAlertMessage("Admin session expired. Please log in again.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/changeEmail/${adminId}`,
        {
          oldEmail: values.oldEmail,
          newEmail: values.newEmail,
        },
        { headers: adminAuthHeaders() }
      );

      if (response.status === 200 && response.data.success) {
        setAlertSeverity("success");
        setAlertMessage(response.data.message || "Email changed successfully.");
        setOpenSnackbar(true);
        setTimeout(() => {
          setOpenSnackbar(false);
          clearAdminSession();
          navigate("/");
        }, 2000);
      } else {
        setAlertSeverity("error");
        setAlertMessage(response.data.message || "Failed to change email. Please try again.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("API Error:", error);
      setAlertSeverity("error");
      setAlertMessage(getApiErrorMessage(error, "Failed to change email. Please try again."));
      setOpenSnackbar(true);
    }
  };

  return (
    <Box sx={adminScrollPageSx}>
      {/* Header Section - Using same gradient as Dashboard */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <MailOutlineIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Change Email Address
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Update your account email address securely
        </Typography>
      </Box>

      {/* Form Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          borderRadius: 2,
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={emailSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box display="grid" gap="20px">
                <TextField
                  fullWidth
                  variant="filled"
                  type="email"
                  label="Current Email Address"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.oldEmail}
                  name="oldEmail"
                  error={!!touched.oldEmail && !!errors.oldEmail}
                  helperText={touched.oldEmail && errors.oldEmail}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          <EmailOutlined />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: alpha(colors.blueAccent[100], 0.5),
                      '&:hover': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="email"
                  label="New Email Address"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.newEmail}
                  name="newEmail"
                  error={!!touched.newEmail && !!errors.newEmail}
                  helperText={touched.newEmail && errors.newEmail}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          <EmailOutlined />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: alpha(colors.blueAccent[100], 0.5),
                      '&:hover': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="email"
                  label="Confirm New Email Address"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmNewEmail}
                  name="confirmNewEmail"
                  error={!!touched.confirmNewEmail && !!errors.confirmNewEmail}
                  helperText={touched.confirmNewEmail && errors.confirmNewEmail}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          <EmailOutlined />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: alpha(colors.blueAccent[100], 0.5),
                      '&:hover': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      },
                      '&.Mui-focused': {
                        backgroundColor: alpha(colors.blueAccent[200], 0.7),
                      }
                    }
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="flex-end" mt="20px">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: colors.blueAccent[500],
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    '&:hover': {
                      backgroundColor: colors.blueAccent[600],
                    }
                  }}
                >
                  Update Email
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={alertSeverity}
          elevation={6}
          variant="filled"
          sx={{
            backgroundColor: alertSeverity === 'success' ? '#4caf50' : '#f44336',
            color: 'white'
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const emailSchema = yup.object().shape({
  oldEmail: yup
    .string()
    .email("Invalid email format")
    .required("Current Email Address is required"),
  newEmail: yup
    .string()
    .email("Invalid email format")
    .required("New Email Address is required")
    .notOneOf(
      [yup.ref("oldEmail")],
      "New Email Address must be different from the current one"
    ),
  confirmNewEmail: yup
    .string()
    .email("Invalid email format")
    .oneOf([yup.ref("newEmail"), null], "Emails must match")
    .required("Confirm New Email Address is required"),
});

const initialValues = {
  oldEmail: "",
  newEmail: "",
  confirmNewEmail: "",
};

export default ChangeEmail;